import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import SummaryCardImage from '../SummaryCardImage'

import { trackClientEvent } from 'analytics/analytics'
import TextButton from 'LuxKit/Button/TextButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ModalContext from 'contexts/ModalContext'
import ExploreBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkButton'
import ExploreBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkToItineraryButton'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import ExploreItemDetails from 'tripPlanner/components/TripModal/ExploreItems/ExploreItemDetails'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import type { ExploreItem } from 'tripPlanner/types/common'
import AboveCardCloseButton from '../Common/AboveCardCloseButton'

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: white;
  max-width: ${rem(300)};
  pointer-events: auto;
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
  }
`

const ImageContainer = styled.div`
  width: 100%;

  > * {
    height: 100%;
    aspect-ratio: 16/9;
  }

  ${mediaQueryUp.largeDesktop} {
    width: 33%;
  }
`
const ContentWrapper = styled.div`
  padding: ${rem(16)};
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  gap: ${rem(8)};

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(24)};
  }
`

const MainGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQueryUp.largeDesktop} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`

interface Props {
  item: ExploreItem
  showCloseButton?: boolean
  onClose?: () => void
}

const BookmarkButtonContainer = styled(Group)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

function ExploreSummaryCard({ item, showCloseButton, onClose }: Props) {
  const intl = useIntl()
  const { place, name, rating, userRatingCount } = item
  const { region } = place

  const showModal = useContext(ModalContext)

  const tripId = useTripId()

  const openDetailsModal = useCallback(() => {
    showModal(<ExploreItemDetails item={item} tripId={tripId} />)

    trackClientEvent({
      action: 'click',
      subject: 'view_more_details_explore_item',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    })
  }, [item, tripId, showModal])

  return (
    <Container>
      {showCloseButton && (
        <AboveCardCloseButton onClick={onClose} />
      )}
      <MainGroup>
        <ImageContainer>
          <SummaryCardImage
            place={place}
            tripId={tripId}
            tripItemId={item.id}
          />
        </ImageContainer>
        <ContentWrapper>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="small">{region}</BodyTextBlock>

            <HeadingTextBlock lineClamp={2} variant="heading6">
              {name}
            </HeadingTextBlock>

            {rating && (
              <GooglePlaceRatingDisplay
                rating={rating}
                ratingsCount={userRatingCount}
                size="S"
              />
            )}
          </Group>

          <TextButton
            kind="tertiary"
            size="small"
            variant="default"
            horizontalOutdent="start"
            nonInteractable
            onClick={openDetailsModal}
          >
            {intl.formatMessage({
              id: 'MZfUZy71t6nxmX7ga14V',
              defaultMessage: 'View details',
              description: 'Button to view details of an explore item on the map summary card',
            })}
          </TextButton>
        </ContentWrapper>
      </MainGroup>
      <BookmarkButtonContainer
        direction="horizontal"
        horizontalAlign="end"
        gap={8}
      >
        <ExploreBookmarkToItineraryButton
          exploreItem={item}
          variant="chip"
          dropdownPlacement="top"
        />
        <ExploreBookmarkButton exploreItem={item} variant="icon" />
      </BookmarkButtonContainer>
    </Container>
  )
}

export default ExploreSummaryCard
